import { Head } from "@inertiajs/react"

import AttributeDisplay from "@/components/ui/attribute-display"
import AttributesContainer from "@/components/ui/attributes-container"
import RecordPageHeader from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import { formatCurrency } from "@/lib/utils"
import { adminAgentArchetypesPath, editAdminAgentArchetypePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface AgentArchetype {
  uid: string
  name: string
  description: string | null
  category: string | null
  icon: string | null
  outcome: string | null
  wholesale_price_cents: number
  state: string
  illustration_svg: string | null
  agents_count: number
  created_at: string
}

const OUTCOME_LABELS: Record<string, string> = {
  "increase-sales": "Increase Sales",
  "boost-productivity": "Boost Productivity",
  "improve-retention": "Improve Retention",
}

export default function AgentArchetypeShow({
  agent_archetype,
}: {
  agent_archetype: AgentArchetype
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Archetypes", href: adminAgentArchetypesPath() },
    { title: agent_archetype.name, href: "#" },
  ]

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={agent_archetype.name} />
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-6 p-4">
        <RecordPageHeader
          title={agent_archetype.name}
          state={agent_archetype.state}
          editPath={editAdminAgentArchetypePath({ id: agent_archetype.uid })}
        />
        {agent_archetype.illustration_svg && (
          <div
            className="text-muted-foreground h-24 w-full max-w-xs overflow-hidden rounded-md border"
            dangerouslySetInnerHTML={{
              __html: agent_archetype.illustration_svg,
            }}
          />
        )}
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Details</h2>
          <AttributesContainer className="gap-6">
            {agent_archetype.description && (
              <AttributeDisplay
                label="Description"
                value={agent_archetype.description}
              />
            )}
            {agent_archetype.category && (
              <AttributeDisplay
                label="Category"
                value={agent_archetype.category}
              />
            )}
            {agent_archetype.icon && (
              <AttributeDisplay label="Icon" value={agent_archetype.icon} />
            )}
            {agent_archetype.outcome && (
              <AttributeDisplay
                label="Outcome"
                value={
                  OUTCOME_LABELS[agent_archetype.outcome] ??
                  agent_archetype.outcome
                }
              />
            )}
            <AttributeDisplay
              label="Wholesale price"
              value={`${formatCurrency(agent_archetype.wholesale_price_cents / 100)}/mo`}
            />
            <AttributeDisplay
              label="Deployed agents"
              value={String(agent_archetype.agents_count)}
            />
            <AttributeDisplay
              label="Created"
              value={new Date(agent_archetype.created_at).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                },
              )}
            />
          </AttributesContainer>
        </div>
      </div>
    </AdminLayout>
  )
}
